/* eslint-disable jsx-a11y/anchor-is-valid */
import type { FC } from "react";
import { useState, useEffect, useCallback } from "react";
import { TextInput, Tooltip } from "flowbite-react";
import { HiOutlinePlus, HiOutlineInformationCircle } from "react-icons/hi";
import {
  StandardForm,
  EntityFormDialog,
  SelectCountry,
  ToggleSwitch,
} from "@components";
import { ICompany } from "../../types";
import { useTranslation } from "react-i18next";
import { Company } from "../../models";

export interface CompanyModalProp {
  data: ICompany;
  isShowing: boolean;
  onConfirm: (data: ICompany) => void;
  onCancel: () => void;
  /** Hide the secondary cancel button (used by the onboarding wizard). */
  hideCancel?: boolean;
  /** Show a processing state on the confirm button while creating. */
  isSubmitting?: boolean;
  /**
   * Suppress the form's built-in description. The onboarding wizard already
   * explains the step in its own heading, so the extra multi-company blurb is
   * redundant noise there.
   */
  hideDescription?: boolean;
}

export const CompanyForm: FC<CompanyModalProp> = function ({
  data,
  isShowing,
  onConfirm,
  onCancel,
  hideCancel,
  isSubmitting,
  hideDescription,
}) {
  const { t } = useTranslation(["common", "company", "modals"]);
  const [isNew] = useState(Company.isNew(data));
  const [isVatRegistered, setIsVatRegistered] = useState(false);
  const [forwardInvoice, setForwardInvoice] = useState(false);

  // Sync with data when modal opens
  useEffect(() => {
    setIsVatRegistered(data.paymentTerms?.isVatRegistered === true);
    setForwardInvoice(data.accountingSettings?.forwardInvoice === true);
  }, [data, isShowing]);

  const handleConfirm = useCallback(
    (formData: ICompany) => {
      if (!formData.paymentTerms) {
        formData.paymentTerms = {
          accountNumber: "",
          iban: "",
          swift: "",
          paymentDays: 0,
          isVatRegistered: false,
        };
      }

      if (!formData.accountingSettings) {
        formData.accountingSettings = {
          accountingEmail: "",
          forwardInvoice: false,
        };
      }

      formData.paymentTerms.isVatRegistered = isVatRegistered;
      formData.accountingSettings.forwardInvoice = forwardInvoice;
      onConfirm(formData);
    },
    [onConfirm, isVatRegistered, forwardInvoice],
  );

  return (
    <StandardForm
      showHeader={false}
      description={
        hideDescription ? undefined : t("modals:descriptions.companies")
      }
      fields={[
        {
          id: "name",
          label: t("common:name"),
          input: (
            <TextInput
              id="name"
              name="name"
              placeholder={t("common:placeholder.company_name")}
              defaultValue={data.name ?? ""}
              type="text"
              required={true}
            />
          ),
        },
        {
          id: "organizationNumber",
          label: t("common:organization_number"),
          input: (
            <TextInput
              id="organizationNumber"
              name="organizationNumber"
              placeholder={t("common:placeholder.organization_number")}
              defaultValue={data.organizationNumber}
            />
          ),
        },
        {
          id: "contactEmail",
          label: (
            <div className="flex items-center">
              {t("common:email")}
              <Tooltip content={t("common:tooltips.email")} trigger="click">
                <HiOutlineInformationCircle
                  size={16}
                  className="ml-2 text-gray-700 hover:text-black transition cursor-pointer"
                />
              </Tooltip>
            </div>
          ),
          input: (
            <TextInput
              id="contactEmail"
              name="contactEmail"
              placeholder={t("common:placeholder.email")}
              defaultValue={data.contactEmail ?? ""}
            />
          ),
        },
        {
          id: "invoiceEmail",
          label: (
            <div className="flex items-center">
              {t("common:invoice_email")}
              <Tooltip
                content={t("common:tooltips.invoice_email")}
                trigger="click"
              >
                <HiOutlineInformationCircle
                  size={16}
                  className="ml-2 text-gray-700 hover:text-black transition cursor-pointer"
                />
              </Tooltip>
            </div>
          ),
          input: (
            <TextInput
              id="invoiceEmail"
              name="invoiceEmail"
              placeholder={t("common:placeholder.email")}
              defaultValue={data.invoiceEmail ?? ""}
            />
          ),
        },
        {
          id: "accountingSettings.accountingEmail",
          label: (
            <div className="flex items-center">
              {t("common:accounting_email")}
              <Tooltip
                content={t("common:tooltips.accounting_email")}
                trigger="click"
              >
                <HiOutlineInformationCircle
                  size={16}
                  className="ml-2 text-gray-700 hover:text-black transition cursor-pointer"
                />
              </Tooltip>
            </div>
          ),
          input: (
            <TextInput
              id="accountingSettings.accountingEmail"
              name="accountingSettings.accountingEmail"
              placeholder={t("common:placeholder.accounting_email")}
              defaultValue={data.accountingSettings?.accountingEmail ?? ""}
            />
          ),
        },
        {
          id: "paymentTerms.paymentDays",
          label: (
            <div className="flex items-center">
              {t("common:payment_days")}
              <Tooltip
                content={t("common:tooltips.payment_days")}
                trigger="click"
              >
                <HiOutlineInformationCircle
                  size={16}
                  className="ml-2 text-gray-700 hover:text-black transition cursor-pointer"
                />
              </Tooltip>
            </div>
          ),
          input: (
            <TextInput
              id="paymentTerms.paymentDays"
              name="paymentTerms.paymentDays"
              placeholder={t("common:placeholder.payment_days")}
              defaultValue={data.paymentTerms?.paymentDays ?? ""}
            />
          ),
        },
        {
          id: "paymentTerms.accountNumber",
          label: (
            <div className="flex items-center">
              {t("common:account_number")}
              <Tooltip
                content={t("common:tooltips.account_number")}
                trigger="click"
              >
                <HiOutlineInformationCircle
                  size={16}
                  className="ml-2 text-gray-700 hover:text-black transition cursor-pointer"
                />
              </Tooltip>
            </div>
          ),
          input: (
            <TextInput
              id="paymentTerms.accountNumber"
              name="paymentTerms.accountNumber"
              placeholder={t("common:placeholder.account_number")}
              defaultValue={data.paymentTerms?.accountNumber ?? ""}
            />
          ),
        },
        {
          id: "paymentTerms.iban",
          label: (
            <div className="flex items-center">
              {t("common:iban")}
              <Tooltip content={t("common:tooltips.iban")} trigger="click">
                <HiOutlineInformationCircle
                  size={16}
                  className="ml-2 text-gray-700 hover:text-black transition cursor-pointer"
                />
              </Tooltip>
            </div>
          ),
          input: (
            <TextInput
              id="paymentTerms.iban"
              name="paymentTerms.iban"
              placeholder={t("common:placeholder.iban")}
              defaultValue={data.paymentTerms?.iban ?? ""}
            />
          ),
        },
        {
          id: "paymentTerms.swift",
          label: (
            <div className="flex items-center">
              {t("common:swift")}
              <Tooltip content={t("common:tooltips.swift")} trigger="click">
                <HiOutlineInformationCircle
                  size={16}
                  className="ml-2 text-gray-700 hover:text-black transition cursor-pointer"
                />
              </Tooltip>
            </div>
          ),
          input: (
            <TextInput
              id="paymentTerms.swift"
              name="paymentTerms.swift"
              placeholder={t("common:placeholder.swift")}
              defaultValue={data.paymentTerms?.swift ?? ""}
            />
          ),
        },
        {
          id: "primaryAddress.addressLine",
          label: t("common:address_line"),
          input: (
            <TextInput
              id="primaryAddress.addressLine"
              name="primaryAddress.addressLine"
              placeholder={t("common:placeholder.address_line")}
              defaultValue={data.primaryAddress?.addressLine ?? ""}
            />
          ),
        },
        {
          id: "primaryAddress.postCode",
          label: t("common:postcode"),
          input: (
            <TextInput
              id="primaryAddress.postCode"
              name="primaryAddress.postCode"
              placeholder={t("common:placeholder.postcode")}
              defaultValue={data.primaryAddress?.postCode ?? ""}
            />
          ),
        },
        {
          id: "primaryAddress.city",
          label: t("common:city"),
          input: (
            <TextInput
              id="primaryAddress.city"
              name="primaryAddress.city"
              placeholder={t("common:placeholder.city")}
              defaultValue={data.primaryAddress?.city ?? ""}
            />
          ),
        },
        {
          id: "primaryAddress.countryCode",
          label: t("common:country"),
          input: (
            <SelectCountry
              id="primaryAddress.countryCode"
              defaultValue={data.primaryAddress?.countryCode ?? "NO"}
            />
          ),
        },
        {
          id: "toggles",
          label: "",
          grid_style: "col-span-2",
          input: (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* VAT Registration Toggle */}
              <div
                className={`p-4 rounded-lg transition-colors duration-200 border ${
                  isVatRegistered
                    ? "bg-brand-muted dark:bg-purple-900/20 border-brand-muted dark:border-purple-900/30"
                    : "bg-surface-muted/30 border-border-strong"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-base font-medium text-text">
                      {t("common:is_vat_registered")}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {isVatRegistered
                        ? t("common:vat_registered_description")
                        : t("common:not_vat_registered_description")}
                    </p>
                  </div>

                  <ToggleSwitch
                    checked={isVatRegistered}
                    onChange={() => setIsVatRegistered(!isVatRegistered)}
                    activeColorClass="bg-lgb-primary"
                    trackModifierClassName="ml-4"
                    preventDefault
                    ariaLabelledBy="vat-label"
                  />
                </div>
              </div>

              {/* Forward Invoice Toggle */}
              <div
                className={`p-4 rounded-lg transition-colors duration-200 border ${
                  forwardInvoice
                    ? "bg-brand-muted dark:bg-purple-900/20 border-brand-muted dark:border-purple-900/30"
                    : "bg-surface-muted/30 border-border-strong"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-base font-medium text-text">
                      {t("common:forward_invoice")}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {forwardInvoice
                        ? t("common:forward_invoice_enabled_description")
                        : t("common:forward_invoice_disabled_description")}
                    </p>
                  </div>

                  <ToggleSwitch
                    checked={forwardInvoice}
                    onChange={() => setForwardInvoice(!forwardInvoice)}
                    activeColorClass="bg-lgb-primary"
                    trackModifierClassName="ml-4"
                    preventDefault
                    ariaLabelledBy="forward-invoice-label"
                  />
                </div>
              </div>
            </div>
          ),
        },
      ]}
      onConfirm={handleConfirm}
      onCancel={onCancel}
      hideCancelButton={hideCancel}
      isSubmitting={isSubmitting}
      buttonConfirmText={t("modals:button_texts.company")}
      buttonConfirmPrependIcon={
        isNew ? <HiOutlinePlus className="mr-2 h-4 w-4" /> : null
      }
      buttonCloseText={t("common:close")}
    />
  );
};

export const CompanyModal: FC<CompanyModalProp> = function (props) {
  const { t } = useTranslation(["common", "company"]);

  return (
    <EntityFormDialog
      title={
        Company.isNew(props.data)
          ? t("company:create_company")
          : t("company:update_company")
      }
      show={props.isShowing}
      onCancel={props.onCancel}
    >
      <CompanyForm {...props} />
    </EntityFormDialog>
  );
};
